import React from 'react';

// ─── 颜色解析与校验工具 ───────────────────────────────────────────

/**
 * 校验 HEX 颜色
 * 合法：#rgb / #rrggbb（大小写不敏感）
 * 非法：位数不对、后面紧跟英文字母、前面紧跟英文字母
 */
function parseHex(raw: string, before: string, after: string): string | null {
  // 前面不能紧跟英文字母（如 abc#123 中的 c 紧贴 #）
  if (/[a-zA-Z]$/.test(before)) return null;
  // 后面不能紧跟英文字母（如 #123text 中的 t）
  if (/^[a-zA-Z]/.test(after)) return null;

  const hex = raw.slice(1); // 去掉 #
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null; // 含非法字符如 G
  if (hex.length === 3) {
    // 短 HEX 展开
    const [r, g, b] = hex.split('').map((c) => parseInt(c + c, 16));
    return `rgb(${r}, ${g}, ${b})`;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgb(${r}, ${g}, ${b})`;
  }
  return null; // 位数不对（#12 #1234 #12345 #12345678...）
}

/**
 * 校验 RGB 颜色
 * rgb(r, g, b)，每个分量 0-255，必须 3 个参数，前面不能紧跟英文字母
 */
function parseRgb(raw: string, before: string): string | null {
  // 前面不能紧跟英文字母（如 theme-rgb / colorrhsl）
  if (/[a-zA-Z-]$/.test(before)) return null;

  const m = raw.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
  if (!m) return null;
  const [r, g, b] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (r > 255 || g > 255 || b > 255) return null;
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * 校验 HSL 颜色
 * hsl(h, s%, l%)，h: 0-360，s/l: 0%-100%，百分号必须有，前面不能紧跟英文字母
 */
function parseHsl(raw: string, before: string): string | null {
  if (/[a-zA-Z-]$/.test(before)) return null;

  const m = raw.match(/^hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)$/i);
  if (!m) return null;
  const h = Number(m[1]);
  const s = Number(m[2]);
  const l = Number(m[3]);
  if (h > 360 || s > 100 || l > 100) return null;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

// ─── 文本分词与渲染 ───────────────────────────────────────────────

interface Token {
  type: 'text' | 'color';
  value: string;       // 原始文本
  color?: string;      // 合法颜色的 CSS 值
}

/**
 * 将文本解析为 Token 列表：
 * 识别 HEX / RGB / HSL，校验合法性后标记为 color token
 */
export function tokenizeColorText(text: string): Token[] {
  const tokens: Token[] = [];

  // 匹配顺序：先 rgb/hsl（带括号，较长），再 hex（较短）
  // 用一个大正则一次性扫描，避免 URL hash 误匹配
  const pattern =
    /(?<![a-zA-Z])(rgb\(\s*-?\d+\s*,\s*-?\d+\s*,\s*-?\d+\s*\))|(?<![a-zA-Z])(hsl\(\s*\d+\s*,\s*\d+%?\s*,\s*\d+%?\s*\))|(#[0-9a-fA-F]+)/gi;

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const start = match.index;
    const raw = match[0];

    // 把匹配前的纯文本加入
    if (start > lastIndex) {
      tokens.push({ type: 'text', value: text.slice(lastIndex, start) });
    }

    const before = text.slice(0, start);
    const after = text.slice(start + raw.length);

    let cssColor: string | null = null;

    if (raw.startsWith('#')) {
      cssColor = parseHex(raw, before, after);
    } else if (/^rgb\(/i.test(raw)) {
      cssColor = parseRgb(raw, before);
    } else if (/^hsl\(/i.test(raw)) {
      cssColor = parseHsl(raw, before);
    }

    if (cssColor) {
      tokens.push({ type: 'color', value: raw, color: cssColor });
    } else {
      tokens.push({ type: 'text', value: raw });
    }

    lastIndex = start + raw.length;
  }

  // 剩余尾部文本
  if (lastIndex < text.length) {
    tokens.push({ type: 'text', value: text.slice(lastIndex) });
  }

  return tokens;
}

// ─── 色卡组件 ────────────────────────────────────────────────────

interface SwatchProps {
  color: string; // 合法的 CSS 颜色值
}

function Swatch({ color }: SwatchProps) {
  const [tooltip, setTooltip] = React.useState(false);

  return (
    <span
      className="relative inline-flex items-center mx-0.5 align-middle"
      style={{ verticalAlign: 'middle' }}
    >
      <span
        className="inline-block rounded-sm border border-black/15 shadow-sm cursor-pointer flex-shrink-0"
        style={{
          backgroundColor: color,
          width: '1em',
          height: '1em',
          boxShadow: '0 0 0 1px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)',
        }}
        title={color}
        onMouseEnter={() => setTooltip(true)}
        onMouseLeave={() => setTooltip(false)}
      />
      {tooltip && (
        <span
          className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 rounded text-xs whitespace-nowrap pointer-events-none"
          style={{
            backgroundColor: 'rgba(17,24,39,0.92)',
            color: '#fff',
            fontSize: '11px',
            lineHeight: '1.4',
          }}
        >
          {color}
        </span>
      )}
    </span>
  );
}

// ─── 主导出：带色卡的富文本渲染 ──────────────────────────────────

interface ColorTextProps {
  children: string;
  className?: string;
}

/**
 * 将文本中的合法颜色值替换为「原文 + 色卡」的组合展示。
 * 用法：<ColorText>文字中的 #3B82F6 会显示色卡</ColorText>
 */
export function ColorText({ children, className }: ColorTextProps) {
  const tokens = tokenizeColorText(children);

  return (
    <span className={className}>
      {tokens.map((token, i) => {
        if (token.type === 'color' && token.color) {
          return (
            <React.Fragment key={i}>
              <span>{token.value}</span>
              <Swatch color={token.color} />
            </React.Fragment>
          );
        }
        return <React.Fragment key={i}>{token.value}</React.Fragment>;
      })}
    </span>
  );
}

export default ColorText;
